/* eslint-disable semi */
import { createDecorator } from 'vue-class-component';
import * as utils from './utils';
import DecoratorConfigs from './DecoratorConfigs';

const debug = process.env.NODE_ENV === 'development';

const DECORATOR_NAME = 'BindAutofocus';

/**
 * そのコンポーネントがフォーカス管理することを宣言します
 *
 * 下記のパラメータを指定できます。
 * refs: フォーカス設定対象コンポーネントの一覧を指定します。
 * detect: autofocus属性付き要素の自動検索を有効にするか指定します。
 * resetBefore: 実行前にフォーカス設定を行う処理を指定します。
 *
 * mountedとresetBeforeで指定された処理時に、フォーカス設定を行います。
 *
 * refs、detectの順でフォーカス設定対象の要素を検索し、フォーカスを設定します。
 * 設定対象の要素がdisabledの場合は、次に優先順位の高い要素にフォーカスを設定
 * します。
 *
 * refsに含まれるコンポーネントは、focusメソッドが実装されているコンポーネント
 * でなければなりません。
 */
export default function BindAutofocus (...args) {
  if (utils.isDecoratorCall(args)) {
    createAppDecorator()(...args);
  } else {
    return createAppDecorator(...args);
  }
}

/**
 * autofocus属性の指定されているVueコンポーネントにフォーカスを設定する
 * @param {*} node
 */
function detectAutofocus (vue) {
  if (vue.autofocus && !vue.disabled && !vue.readonly && vue.focus) {
    vue.focus();
    return true;
  }
  const children = vue.$children;
  for (let idx in children) {
    if (detectAutofocus(children[idx])) {
      return true;
    }
  }
}

/**
 * フォーカスを設定する
 * @param {*} vue
 * @param {*} detect
 */
function setFocus (vue, detect) {
  // autofocus属性の指定されている要素にフォーカスを設定する
  if (detect) {
    detectAutofocus(vue);
  }
}

function createAppDecorator (decoratorOptions = {}) {
  // 設定値のマージ
  decoratorOptions = DecoratorConfigs.marge(DECORATOR_NAME, decoratorOptions);

  // 実装時の整合性チェック
  if (debug) {
    utils.validateDecoratorOptions(
      DECORATOR_NAME, {
        detect: { type: 'boolean' },
        refs: { type: 'array', shouldBe: ['not empty'] },
        resetBefore: { type: 'array' }
      },
      decoratorOptions
    );
  }

  return createDecorator((options, key) => {
    // 実装時の整合性チェック
    if (debug) {
      console.debug(`${DECORATOR_NAME}: @ ${key}`);

      if (key !== 'data') {
        throw new Error(`${DECORATOR_NAME}: can decorate only data().`);
      }
    }

    // オプション値取得
    const detect = decoratorOptions.detect || false;
    const resetBefore = decoratorOptions.resetBefore || [];

    // mountedのタイミングで事前処理を追加する
    const mounted = options.mounted || function () { };
    options.mounted = utils.injectBefore(mounted, function () {
      // iOSで表示できないエラー対応
      setTimeout(() => {
        setFocus(this, detect);
      }, 1);
    });

    // 関数をラップして事前処理を追加する
    utils.wrapMethods(options.methods, resetBefore, function () {
      // iOSで表示できないエラー対応
      setTimeout(() => {
        setFocus(this, detect);
      }, 1);
    });
  });
}



// WEBPACK FOOTER //
// ./src/core/decorators/BindAutofocus.js